import { Icon } from '@fctg/core-ui-component';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useAtomValue } from 'jotai';
import type { FC } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { APP_NAME } from '@constants/app';
import { navItems } from '@constants/routes/navItems';
import { sidebarCollapsedAtom } from '@/store/layoutAtoms';
import SidebarUser from './SidebarUser';
import styles from './styles.module.scss';

const { Sider } = Layout;

const items: MenuProps['items'] = navItems.map(({ path, icon, label }) => ({
	key: path,
	icon,
	label,
}));

/**
 * A page's tabs live under its own path (/apps/assignments), so the menu matches
 * on the first segment rather than the whole pathname — otherwise being on a tab
 * would leave nothing selected.
 */
const selectedKeyFor = (pathname: string): string => {
	const [, segment = ''] = pathname.split('/');

	return navItems.find((item) => item.segment === segment)?.path ?? pathname;
};

const Sidebar: FC = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const isSidebarCollapsed = useAtomValue(sidebarCollapsedAtom);

	const handleClick: MenuProps['onClick'] = ({ key }) => {
		navigate(key);
	};

	return (
		<Sider
			className={styles.sider}
			width={200}
			collapsedWidth={80}
			collapsed={isSidebarCollapsed}
			trigger={null}
		>
			<div className={`${styles.brand} ${isSidebarCollapsed ? styles.brandCollapsed : ''}`}>
				{!isSidebarCollapsed && <span className={styles.brandName}>{APP_NAME}</span>}
				<Icon name={isSidebarCollapsed ? 'LogoCollapsed' : 'LogoExpanded'} />
			</div>

			<Menu
				theme="dark"
				mode="inline"
				className={styles.menu}
				selectedKeys={[selectedKeyFor(pathname)]}
				items={items}
				onClick={handleClick}
			/>

			<SidebarUser />
		</Sider>
	);
};

export default Sidebar;
